/**
 * Copilot panel (⌘J, blueprint §9.1, prototype #copilot): the top-bar button plus the slide-in
 * panel. Answers stream over SSE from POST /copilot/threads/{id}/messages; citation tokens
 * ([[campaign:ref|Name]], [[product:uuid|Name]]…) render as links to the screen the number came
 * from, and drafts land in the user's tray (nothing is published from here).
 * Other screens can open it with `window.dispatchEvent(new CustomEvent("copilot:open", { detail: "question" }))`.
 */
import type { AiStatus, CopilotDraft, CopilotItem, CopilotThreadResponse } from "@brecx/shared";
import { Fragment, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { api, uuid } from "../lib/api";
import { useRange } from "../lib/range";
import { useSession } from "../lib/session";
import { Icon } from "./Icon";
import "./Copilot.css";

const SUGGESTIONS = [
  "Where did we waste money this week?",
  "Which campaigns lost the most profit vs the previous period?",
  "Which search terms should be negatives?",
];
const THREAD_KEY = "brecx-copilot-thread";

function remember(id: string | null) {
  try {
    if (id) sessionStorage.setItem(THREAD_KEY, id);
    else sessionStorage.removeItem(THREAD_KEY);
  } catch {
    /* storage unavailable: the thread just isn't restored */
  }
}
function recall(): string | null {
  try {
    return sessionStorage.getItem(THREAD_KEY);
  } catch {
    return null;
  }
}

/** Where a citation points. */
function citeHref(kind: string, ref: string, query: string): string | null {
  switch (kind) {
    case "campaign":
      return `/campaigns?c=${encodeURIComponent(ref)}&${query}`;
    case "product":
      return `/products/${encodeURIComponent(ref)}?${query}`;
    case "ad_group":
      return `/ads?adGroup=${encodeURIComponent(ref)}&${query}`;
    case "search_term":
      return `/search-terms?q=${encodeURIComponent(ref.replace(/^"|"$/g, ""))}&${query}`;
    case "report":
      return `/reports?${query}`;
    default:
      return null;
  }
}

const TOKEN = /\[\[(\w+):([^\]|]+)(?:\|([^\]]+))?\]\]|\*\*([^*]+)\*\*/g;

function inline(text: string, query: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(TOKEN)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[4]) out.push(<b key={m.index}>{m[4]}</b>);
    else {
      const [, kind = "", ref = "", label] = m;
      const href = citeHref(kind, ref.trim(), query);
      const shown = label?.trim() || `${kind}:${ref.trim().replace(/^[0-9a-f-]{36}:/i, "")}`;
      out.push(
        href ? (
          <Link key={m.index} className="cite" to={href}>
            {shown}
          </Link>
        ) : (
          <span key={m.index} className="cite">
            {shown}
          </span>
        ),
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** Paragraphs, "- " bullet lists, **bold** and citation tokens. */
export function Answer({ text, query }: { text: string; query: string }) {
  const blocks = text.trim().split(/\n{2,}/);
  return (
    <>
      {blocks.map((b, i) => {
        const lines = b.split("\n");
        if (lines.every((l) => /^\s*[-*•] /.test(l)))
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: blocks of one message never reorder
            <ul key={i}>
              {lines.map((l, j) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: lines of one block never reorder
                <li key={j}>{inline(l.replace(/^\s*[-*•] /, ""), query)}</li>
              ))}
            </ul>
          );
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: blocks of one message never reorder
          <p key={i}>
            {lines.map((l, j) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: lines of one block never reorder
              <Fragment key={j}>
                {j ? <br /> : null}
                {inline(l, query)}
              </Fragment>
            ))}
          </p>
        );
      })}
    </>
  );
}

function DraftCard({ d, query }: { d: CopilotDraft; query: string }) {
  return (
    <div className="draftcard">
      <b>Draft · {d.title}</b>
      {d.ops.length ? (
        <ul className="cop-ops">
          {d.ops.map((o) => (
            <li key={o}>{o}</li>
          ))}
        </ul>
      ) : (
        <span className="t2">No ops were added.</span>
      )}
      {d.skipped.length ? <span className="t2">Skipped: {d.skipped.join(" · ")}</span> : null}
      <span className="t2">
        In your drafts. Nothing is published until the change set is reviewed and approved.
      </span>
      {d.ops.length ? (
        <div className="row">
          <Link className="btn sm pri" to={`/changes?${d.setId ? `set=${d.setId}&` : ""}${query}`}>
            Review in Changes
          </Link>
        </div>
      ) : null}
    </div>
  );
}

/** Parses a text/event-stream body into (event, data) pairs. */
async function readSse(res: Response, on: (event: string, data: unknown) => void) {
  const reader = res.body!.pipeThrough(new TextDecoderStream()).getReader();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += value;
    let cut = buf.indexOf("\n\n");
    while (cut >= 0) {
      const chunk = buf.slice(0, cut);
      buf = buf.slice(cut + 2);
      let event = "message";
      let data = "";
      for (const line of chunk.split("\n")) {
        if (line.startsWith("event: ")) event = line.slice(7);
        else if (line.startsWith("data: ")) data += line.slice(6);
      }
      if (data) on(event, JSON.parse(data));
      cut = buf.indexOf("\n\n");
    }
  }
}

export function Copilot() {
  const { me, can } = useSession();
  const { days, query } = useRange();
  const allowed = can("analyst");
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [items, setItems] = useState<CopilotItem[]>([]);
  const [live, setLive] = useState("");
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState("");
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const msgs = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const pending = useRef<string | null>(null);

  const toggle = useCallback((next?: boolean) => setOpen((o) => next ?? !o), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
        e.preventDefault();
        toggle();
      } else if (e.key === "Escape") toggle(false);
    };
    const onAsk = (e: Event) => {
      pending.current =
        typeof (e as CustomEvent).detail === "string" ? (e as CustomEvent<string>).detail : null;
      toggle(true);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("copilot:open", onAsk);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("copilot:open", onAsk);
    };
  }, [toggle]);

  // first open: AI status and the thread from this browser session
  useEffect(() => {
    if (!open || !allowed || status) return;
    api
      .get<AiStatus>("/ai/status")
      .then(setStatus)
      .catch((e: Error) => setLoadErr(e.message));
    const id = recall();
    if (id)
      api
        .get<CopilotThreadResponse>(`/copilot/threads/${id}`)
        .then((t) => {
          setThreadId(t.thread.id);
          setItems(t.items);
        })
        .catch(() => remember(null));
  }, [open, allowed, status]);

  useEffect(() => {
    if (open) setTimeout(() => box.current?.focus(), 50);
  }, [open]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll whenever content grows
  useEffect(() => {
    msgs.current?.scrollTo({ top: msgs.current.scrollHeight });
  }, [items, live]);

  const send = useCallback(
    async (text: string) => {
      const q = text.trim();
      if (!q || busy) return;
      setBusy(true);
      setInput("");
      setItems((xs) => [...xs, { kind: "user", text: q }]);
      let answer = "";
      const flush = () => {
        if (answer.trim()) {
          const t = answer;
          setItems((xs) => [...xs, { kind: "assistant", text: t }]);
        }
        answer = "";
        setLive("");
      };
      try {
        let id = threadId;
        if (!id) {
          const t = await api.post<{ id: string }>("/copilot/threads", {});
          id = t.id;
          setThreadId(id);
          remember(id);
        }
        const res = await fetch(`/api/v1/copilot/threads/${id}/messages`, {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "content-type": "application/json",
            accept: "text/event-stream",
            "x-csrf-token": me.csrf,
            "idempotency-key": uuid(),
          },
          body: JSON.stringify({ text: q, days }),
        });
        if (!res.ok || !res.body) {
          const p = (await res.json().catch(() => ({}))) as { detail?: string; title?: string };
          throw new Error(p.detail ?? p.title ?? `Copilot failed (${res.status})`);
        }
        await readSse(res, (event, data) => {
          const d = data as {
            delta?: string;
            summary?: string;
            name?: string;
            draft?: CopilotDraft;
            message?: string;
          };
          if (event === "text") {
            answer += d.delta ?? "";
            setLive(answer);
          } else if (event === "tool") {
            flush();
            setItems((xs) => [...xs, { kind: "tool", name: d.name ?? "", summary: d.summary ?? "" }]);
          } else if (event === "draft" && d.draft) {
            const draft = d.draft;
            setItems((xs) => [...xs, { kind: "draft", draft }]);
            window.dispatchEvent(new Event("drafts:changed"));
          } else if (event === "error") {
            flush();
            setItems((xs) => [...xs, { kind: "error", text: d.message ?? "Something went wrong." }]);
          }
        });
        flush();
      } catch (e) {
        flush();
        setItems((xs) => [...xs, { kind: "error", text: (e as Error).message }]);
      } finally {
        setBusy(false);
      }
    },
    [busy, threadId, me.csrf, days],
  );

  // a question passed with "copilot:open" is asked once AI is known to be available
  useEffect(() => {
    if (open && status?.configured && pending.current) {
      const q = pending.current;
      pending.current = null;
      send(q);
    }
  }, [open, status, send]);

  const reset = () => {
    setThreadId(null);
    setItems([]);
    remember(null);
    box.current?.focus();
  };

  const ready = allowed && status?.configured;
  return (
    <>
      <button
        type="button"
        className="copilot-btn"
        aria-expanded={open}
        aria-controls="copilot"
        onClick={() => toggle()}
      >
        <Icon name="spark" />
        <span>Copilot</span>
        <kbd className="kbd cop-kbd">⌘J</kbd>
      </button>
      <aside
        className={`copilot${open ? " open" : ""}`}
        id="copilot"
        aria-label="Copilot"
        aria-hidden={!open}
        inert={!open}
      >
        <div className="h">
          <Icon name="spark" />
          <b>Copilot</b>
          <span className="chip plain">
            {status ? `${status.models.interactive} · read-only + drafts` : "read-only + drafts"}
          </span>
          <div className="sp" />
          {items.length ? (
            <button type="button" className="btn sm ghost" onClick={reset} disabled={busy}>
              New
            </button>
          ) : null}
          <button type="button" className="icon-btn" aria-label="Close Copilot" onClick={() => toggle(false)}>
            <Icon name="x" />
          </button>
        </div>
        <div className="msgs" ref={msgs} aria-live="polite">
          {!allowed ? (
            <div className="cop-note">Copilot needs the analyst role. Ask a workspace admin for access.</div>
          ) : null}
          {loadErr ? <div className="cop-note err">{loadErr}</div> : null}
          {allowed && status && !status.configured ? (
            <div className="cop-note">
              AI is not configured. Set ANTHROPIC_API_KEY on the API to use Copilot.
            </div>
          ) : null}
          {ready && !items.length && !busy ? (
            <div className="cop-note">
              Ask about profit, products, keywords, search terms and changes. Every number is fetched from
              your data and links to its screen; changes come back as drafts for you to review.
            </div>
          ) : null}
          {items.map((it, i) =>
            it.kind === "user" ? (
              // biome-ignore lint/suspicious/noArrayIndexKey: an append-only transcript
              <div className="msg u" key={i}>
                {it.text}
              </div>
            ) : it.kind === "assistant" ? (
              // biome-ignore lint/suspicious/noArrayIndexKey: an append-only transcript
              <div className="msg a" key={i}>
                <Answer text={it.text} query={query} />
              </div>
            ) : it.kind === "tool" ? (
              // biome-ignore lint/suspicious/noArrayIndexKey: an append-only transcript
              <div className="tool" key={i}>
                <Icon name="search" size={12} />
                {it.summary}
              </div>
            ) : it.kind === "draft" ? (
              // biome-ignore lint/suspicious/noArrayIndexKey: an append-only transcript
              <DraftCard d={it.draft} query={query} key={i} />
            ) : (
              // biome-ignore lint/suspicious/noArrayIndexKey: an append-only transcript
              <div className="cop-note err" role="alert" key={i}>
                {it.text}
              </div>
            ),
          )}
          {live ? (
            <div className="msg a">
              <Answer text={live} query={query} />
            </div>
          ) : busy ? (
            <div className="tool" role="status">
              <Icon name="refresh" size={12} />
              Thinking…
            </div>
          ) : null}
        </div>
        <div className="in">
          {ready && !items.length ? (
            <div className="sugs">
              {SUGGESTIONS.map((s) => (
                <button type="button" key={s} onClick={() => send(s)} disabled={busy}>
                  {s}
                </button>
              ))}
            </div>
          ) : null}
          <textarea
            ref={box}
            value={input}
            disabled={!ready || busy}
            aria-label="Ask Copilot"
            placeholder={
              ready
                ? `Ask about profit, products, search terms… last ${days} days (Enter to send)`
                : allowed && !status
                  ? "Checking AI…"
                  : "Copilot is unavailable"
            }
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(input);
              }
            }}
          />
        </div>
      </aside>
    </>
  );
}
